import { DigestV2State } from "pages/api/deep-links/types";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLibraryWithRouter, ItemType } from "src/components/useLibrary";

interface UseFeedOptions {
  onlyPublished?: boolean;
  itemsPerPage?: number;
}

interface UseFeedResult {
  items: AnnotatedDeepLink<DigestV2State>[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  filteredItems: AnnotatedDeepLink<DigestV2State>[];
  libraryFilter: string;
  setLibraryFilter: (filter: string) => void;
  availableTags: string[];
}
/**
 * Hook to manage feed data loading, pagination, and filtering
 * @param options {UseFeedOptions} Optional filtering and pagination options
 * @returns {UseFeedResult} Feed data, control functions, and filtering state
 */
export function useFeed(options: UseFeedOptions = {}): UseFeedResult {
  // Add Library-based filtering
  const {
    items: libraryItems,
    filter: libraryFilter,
    setFilter: setLibraryFilter,
  } = useLibraryWithRouter({
    path: "home",
    prefilter: (item) => item.itemType === ItemType.TOPICS,
  });

  useEffect(() => {
    if (libraryItems?.length > 0 && !initialLoadDone.current) {
      loadInitial();
      initialLoadDone.current = true;
    }
  }, [libraryItems]);

  // Reload when filter changes
  useEffect(() => {
    loadInitial();
  }, [libraryFilter]);

  //TODO clean this up and just use the topicCardConfig
  const filtered = (libraryItems as AnnotatedDeepLink<DigestV2State>[]).filter(
    (item) => item.json?.status === "published" && item.json?.topicCardConfig
  );

  const availableTags = [
    ...new Set(
      filtered
        .flatMap((item) => item.json?.tags || [])
        .filter((tag): tag is string => tag !== undefined)
        .map((tag) => tag.toLowerCase())
    ),
  ];

  const initialLoadDone = useRef(false);

  // Feed builder to sort cards by frequency and recency
  const feedBuilder = useCallback(
    (items: AnnotatedDeepLink<DigestV2State>[]) => {
      return [...items].sort((a, b) => {
        // Get frequency from card type
        const getFrequency = (item: AnnotatedDeepLink<DigestV2State>) => {
          const tags = item.json?.tags || [];
          const lowerTags = tags.map((tag) => tag.toLowerCase());

          if (lowerTags.some((tag) => tag.includes("daily"))) return 3;
          if (lowerTags.some((tag) => tag.includes("weekly"))) return 2;
          if (lowerTags.some((tag) => tag.includes("monthly"))) return 1;
          return 0;
        };

        // Get most recent date from date range
        const getMostRecentDate = (item: AnnotatedDeepLink<DigestV2State>) => {
          const dateRange = item.json?.defaultDateRange;
          if (!dateRange) return new Date(0);

          // Use end date if available, otherwise use start date
          if (dateRange.endDate) {
            return new Date(dateRange.endDate);
          }
          if (dateRange.startDate) {
            return new Date(dateRange.startDate);
          }

          return new Date(0);
        };

        // First sort by frequency
        const freqA = getFrequency(a);
        const freqB = getFrequency(b);
        if (freqA !== freqB) {
          return freqB - freqA; // Higher frequency first
        }

        // Then sort by most recent date
        const dateA = getMostRecentDate(a);
        const dateB = getMostRecentDate(b);
        return dateB.getTime() - dateA.getTime();
      });
    },
    []
  );

  // Apply feed builder to filtered items
  const sortedItems = useMemo(() => {
    return feedBuilder(filtered);
  }, [filtered, feedBuilder]);

  const {
    items: paginatedItems,
    isLoading,
    hasMore,
    loadMore,
    loadInitial,
  } = usePaginatedItems(sortedItems, { itemsPerPage: 4 });

  return {
    items: paginatedItems,
    isLoading,
    hasMore,
    loadMore,
    filteredItems: libraryItems as AnnotatedDeepLink<DigestV2State>[],
    libraryFilter,
    setLibraryFilter,
    availableTags: availableTags || [],
  };
}

/**
 * Internal hook to manage feed data loading and pagination.
 * this is really just about not loading all cards at once until we push paging to the server side.
 */
function usePaginatedItems(
  filteredItems: AnnotatedDeepLink<DigestV2State>[],
  options: UseFeedOptions = {}
): {
  items: AnnotatedDeepLink<DigestV2State>[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  loadInitial: () => Promise<void>;
} {
  const { onlyPublished = false, itemsPerPage = 10 } = options;
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AnnotatedDeepLink<DigestV2State>[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const loadedIds = useRef(new Set<string>());
  const initialLoadDone = useRef(false);

  const loadMore = useCallback(async () => {
    if (!filteredItems || !hasMore) {
      return;
    }

    const startIndex = 0;
    const endIndex = page * itemsPerPage;

    // 2. Page
    const pageItems = filteredItems.slice(startIndex, endIndex);

    // Update state
    setItems((prev) => [...pageItems]);
    setHasMore(endIndex < filteredItems.length);
    setPage((prev) => prev + 1);
  }, [filteredItems, page, hasMore, itemsPerPage, onlyPublished]);

  const loadInitial = useCallback(async () => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    loadedIds.current.clear();
    initialLoadDone.current = true;

    if (filteredItems?.length > 0) {
      const startIndex = 0;
      const endIndex = itemsPerPage;
      const pageItems = filteredItems.slice(startIndex, endIndex);
      setItems(pageItems);
      setHasMore(endIndex < filteredItems.length);
      setPage(2); // Set to 2 since we've loaded the first page
    }
  }, [filteredItems, itemsPerPage]);

  return {
    items,
    isLoading: false,
    hasMore,
    loadMore,
    loadInitial,
  };
}
